import { adjustedCategory, adjustedCategoryCounts, matchesAdjustedCategories } from "../config/productCategories";
import type { FavoriteProduct } from "../services/FavoritesService";
import type { ProductBrowseResponse } from "../services/ProductsService";
import type { ProductListItem } from "../types/Product";
import {
  getCashbackAmount,
  getOfferType,
  getCashbackMinQty,
  getEffectiveDiscountPercent,
  getLastSeenTimestamp,
  getLastUpdatedTimestamp,
} from "../components/ProductItem.utils";
import type {
  WalmartStore,
  HomeDepotStore,
  DollarGeneralStore,
  TargetStore,
} from "../utils/storeUtils";
import { formatHomeDepotStoreId } from "../utils/storeUtils";
import { sortByBestDeal } from "../utils/bestDealRanking";
import { extraCatalogStoreName, findExtraCatalogStore, formatExtraStoreId } from "../utils/extraStores";
import { hasPlusOrProPlan } from "../utils/retailerStoreLimits";
import { PUBLIC_DEALS_RETAILERS } from "../config/publicDeals";
import { isExtraOnlineRetailer, isOnlineOnlyRetailer } from "../config/retailers";
import { getRetailerAccessOverride, normalizeRetailerAccessSlug } from "../utils/userUtils";

/** Walmart products the server matched to at least one Ibotta offer that has not expired. */
const hasLiveIbottaOffer = (product: ProductListItem): boolean => {
  const offers = Array.isArray(product.ibottaOffers) ? product.ibottaOffers : [];
  const now = Date.now();
  return offers.some((offer) => {
    if (!offer || typeof offer.name !== "string" || !offer.name.trim()) return false;
    if (offer.expired) return false;
    const expiration = offer.expiration ? new Date(offer.expiration).getTime() : NaN;
    return !(Number.isFinite(expiration) && expiration <= now);
  });
};

export type StoreFilterOption = {
  id: string;
  name: string;
  isAll?: boolean;
  isExtra?: boolean;
  distanceMiles?: number;
};

export const isOnlineRetailer = (retailer: string) => {
  return isOnlineOnlyRetailer(retailer);
};

const normalizeOnlineRetailer = (retailer: string) =>
  retailer === "walmart_online"
    ? "walmart"
    : retailer === "target_online"
    ? "target"
    : retailer;

const PUBLIC_ONLINE_FEED_RETAILERS = new Set(
  PUBLIC_DEALS_RETAILERS.map((retailer) => retailer.apiRetailer)
);

export const hasPublicOnlineFeedAccess = (retailer: string, isOnline: boolean) =>
  isOnline && PUBLIC_ONLINE_FEED_RETAILERS.has(normalizeOnlineRetailer(retailer));

export const normalizeSearchToken = (value: string) => value.trim().toLowerCase();

export const buildSearchTokens = (
  searchTerm: string,
  selectedSavedKeywords: string[]
) => {
  const seen = new Set<string>();
  return [searchTerm, ...selectedSavedKeywords]
    .map(normalizeSearchToken)
    .filter((token) => {
      if (!token || seen.has(token)) return false;
      seen.add(token);
      return true;
    });
};

export const getProductSearchHaystack = (product: ProductListItem) =>
  [
    product.name,
    adjustedCategory(product),
    product.upc,
    product.sku,
    product.sellerName,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();

export const matchesSearchTokens = (
  product: ProductListItem,
  tokens: string[]
) => {
  if (!tokens.length) return true;
  const haystack = getProductSearchHaystack(product);
  return tokens.some((token) => haystack.includes(token));
};

export const getApiStoreId = (input: {
  retailer: string;
  selectedStoreId: string;
  isAdmin: boolean;
}) => {
  if (input.retailer === "amazon" || input.retailer === "kohls" || isExtraOnlineRetailer(input.retailer)) return "online";
  if (input.selectedStoreId === "online") return "online";
  if (input.retailer === "bestbuy" || input.retailer === "target" || input.retailer === "lowes" || input.retailer === "tractor_supply" || input.retailer === "northern_tool" || input.retailer === "samsclub") {
    return input.selectedStoreId;
  }
  return input.isAdmin && input.selectedStoreId !== "all"
    ? input.selectedStoreId
    : undefined;
};

export const buildStoreFilterOptions = (input: {
  retailer: string;
  userStoreIds: string[];
  loadedStores?:
    | WalmartStore[]
    | HomeDepotStore[]
    | DollarGeneralStore[]
    | TargetStore[]
    | any[];
}) => {
  const options: StoreFilterOption[] = [{ id: "all", name: "All", isAll: true }];
  if (!input.loadedStores) return options;

  input.userStoreIds.forEach((storeId) => {
    if (storeId === "online") return;
    let storeName = "";
    if (input.retailer === "home_depot") {
      const stores = input.loadedStores as HomeDepotStore[];
      const store = stores.find(
        (s: any) =>
          s.store_number && formatHomeDepotStoreId(s.store_number) === storeId
      );
      storeName = store?.city || "";
    } else if (input.retailer === "dollar_general") {
      const stores = input.loadedStores as DollarGeneralStore[];
      const store = stores.find((s: any) => s.storeNumber === storeId);
      storeName = store?.address || "";
    } else if (input.retailer === "target" || input.retailer === "lowes") {
      const stores = input.loadedStores as TargetStore[];
      const store = stores.find(
        (s: any) => String(s["Store No."] || s.store_id) === storeId
      );
      storeName = store
        ? `${(store as any).Street} ${(store as any).City}`
        : `Store ${storeId}`;
    } else if (input.retailer === "tractor_supply" || input.retailer === "northern_tool" || input.retailer === "samsclub") {
	  const stores = input.loadedStores as any[];
	  const store = stores.find((s: any) => String(s.store_number).replace(/^0+/, "") === storeId.replace(/^0+/, ""));
      storeName = store?.label || (store ? `${store.city || "Store"} - #${storeId}` : `Store ${storeId}`);
    } else if (input.retailer === "bestbuy") {
      const stores = input.loadedStores as any[];
      const store = stores.find((s: any) => s.store_number === storeId);
      storeName = store ? `${store.name} - ${store.city}` : `Store ${storeId}`;
    } else if (input.retailer === "costco") {
      const stores = input.loadedStores as any[];
      const num = parseInt(storeId.replace(/-wh$/, ""), 10);
      const store = stores.find((s: any) => s.warehouse_number === num);
      storeName = store ? `${store.warehouse_name} - ${store.location}` : `Warehouse ${storeId}`;
    } else {
      const stores = input.loadedStores as WalmartStore[];
      const store = stores.find((s: any) => s.store_id === storeId);
      storeName = store ? `${store.address}` : `Store ${storeId}`;
    }

    options.push({ id: storeId, name: storeName || "" });
  });

  return options;
};

// Extra stores follow the saved ones so "All" keeps meaning the saved stores.
// Names match the saved stores of the retailer: Home Depot by city, Walmart by
// catalog address.
export const appendExtraStoreOptions = (
  options: StoreFilterOption[],
  extras: { storeId: string; distanceMiles: number }[] | undefined,
  loadedStores?: HomeDepotStore[] | WalmartStore[] | any[],
  retailer: string = "home_depot"
): StoreFilterOption[] => {
  if (!extras?.length) return options;
  const taken = new Set(options.map((option) => option.id));
  const out = [...options];
  extras.forEach((extra) => {
    const id = formatExtraStoreId(extra.storeId);
    if (!id || taken.has(id)) return;
    taken.add(id);
    const store = findExtraCatalogStore(loadedStores, retailer, id);
    const name = retailer === "home_depot"
      ? extraCatalogStoreName(store, retailer)
      : (typeof store?.address === "string" ? store.address.replace(/\u00a0/g, " ").trim() : "");
    out.push({ id, name: name || `Store ${id}`, isExtra: true, distanceMiles: extra.distanceMiles });
  });
  return out;
};

/** @deprecated Use appendExtraStoreOptions; kept for existing callers. */
export const appendHomeDepotExtraStoreOptions = (
  options: StoreFilterOption[],
  extras: { storeId: string; distanceMiles: number }[] | undefined,
  loadedStores?: HomeDepotStore[] | any[]
): StoreFilterOption[] => appendExtraStoreOptions(options, extras, loadedStores, "home_depot");

export const appendAdminStoreOption = (
  options: StoreFilterOption[],
  selectedStoreId: string,
  loadedStores:
    | WalmartStore[]
    | HomeDepotStore[]
    | DollarGeneralStore[]
    | TargetStore[]
    | any[]
    | undefined,
  retailer: string
): StoreFilterOption[] => {
  if (
    !selectedStoreId ||
    selectedStoreId === "all" ||
    selectedStoreId === "online" ||
    options.some((option) => option.id === selectedStoreId)
  ) {
    return options;
  }

  const customOptions = buildStoreFilterOptions({
    retailer,
    userStoreIds: [selectedStoreId],
    loadedStores,
  });
  const customOption = customOptions.find((option) => option.id === selectedStoreId);
  if (!customOption) {
    return [...options, { id: selectedStoreId, name: `Store #${selectedStoreId}` }];
  }
  return [...options, customOption];
};

export const buildWootProducts = (products: any[]) => {
  return products.map((p) => {
    const currentPrice = p.salesPrice?.[p.salesPrice.length - 1] || 0;
    const msrp = p.listPrice || 0;
    const discountPercentage =
      msrp > 0 ? Math.round(((msrp - currentPrice) / msrp) * 100) : 0;
    const modifiedAt = new Date(p.modifiedAt).getTime();
    return {
      upc: p.Asin,
      sku: p.Asin,
      name: p.title || "Unknown Product",
      msrp,
      onlinePrice: currentPrice,
      image: p.image,
      storeId: "online",
      "storeId-upc": `online-${p.Asin}`,
      storePrice: p.salesPrice?.length > 0 ? p.salesPrice : [currentPrice],
      backroom: [0],
      salesFloor: p.inventory?.length > 0 ? p.inventory : [1],
      aisle: p.isRefurbished ? "REFURBISHED" : "",
      category: p.category || "",
    adjustedCategory: p.adjustedCategory,
      url: p.url,
      // Free accounts get a sealed key in place of the ASIN plus our /go/
      // redirect; the key routes clicks through the affiliate endpoint.
      ...(/^https:\/\/(www\.)?dealhawkpro\.io\/go\//i.test(p.url || "") ? { productKey: p.Asin } : {}),
      discountPercentage,
      firstInserted: modifiedAt,
      lastUpdates:
        p.updateTimes?.length > 0 ? p.updateTimes : [modifiedAt],
      inStock: (p.inventory?.[p.inventory.length - 1] || 0) > 0,
    } as ProductListItem;
  });
};

const getOnlineFirstInserted = (product: {
  firstInserted?: number;
  lastUpdates?: number[];
  lastUpdated?: number;
}) => {
  if (
    typeof product.firstInserted === "number" &&
    Number.isFinite(product.firstInserted) &&
    product.firstInserted > 0
  ) {
    return product.firstInserted;
  }
  const updates = Array.isArray(product.lastUpdates)
    ? product.lastUpdates.filter(
        (timestamp): timestamp is number =>
          typeof timestamp === "number" && Number.isFinite(timestamp) && timestamp > 0
      )
    : [];
  if (updates.length > 0) {
    return Math.min(...updates);
  }
  return product.lastUpdated || 0;
};

export const buildWalmartOnlineProducts = (products: any[]) => {
  return products.map((p) => ({
    productKey: p.productKey,
    upc: p.upc,
    sku: p.sku || p.upc,
    name: p.title,
    msrp: p.msrp,
    onlinePrice: p.msrp,
    image: p.image,
    storeId: "online",
    "storeId-upc": `online-${p.upc}`,
    storePrice: p.currentPrices.length > 0 ? p.currentPrices : [p.currentPrice],
    backroom: [0],
    salesFloor: [1],
    aisle: "",
    category: p.category,
    adjustedCategory: p.adjustedCategory,
    url: p.url,
    discountPercentage: p.discountPercentage,
    sellerName: p.sellerName,
    firstInserted: getOnlineFirstInserted(p),
    lastUpdates: p.lastUpdates.length > 0 ? p.lastUpdates : [p.lastUpdated],
    inStock: true,
  })) as ProductListItem[];
};

export const buildTargetOnlineProducts = (products: any[]) => {
  return products.map((p) => ({
    productKey: p.productKey,
    upc: p.upc,
    sku: p.sku || p.upc,
    name: p.title,
    msrp: p.msrp,
    onlinePrice: p.msrp,
    image: p.image,
    storeId: "online",
    "storeId-upc": `online-${p.upc}`,
    storePrice: p.currentPrices.length > 0 ? p.currentPrices : [p.currentPrice],
    backroom: [0],
    salesFloor: [1],
    aisle: "",
    category: p.category,
    adjustedCategory: p.adjustedCategory,
    url: p.url,
    discountPercentage: p.discountPercentage,
    firstInserted: getOnlineFirstInserted(p),
    lastUpdates: p.lastUpdates.length > 0 ? p.lastUpdates : [p.lastUpdated],
    inStock: true,
  })) as ProductListItem[];
};

export const buildAmazonOnlineProducts = (products: any[]) => {
  return products.map((p) => ({
    upc: p.upc,
    sku: p.sku || p.upc,
    name: p.title,
    msrp: p.msrp,
    onlinePrice: p.msrp,
    image: p.image,
    storeId: "online",
    "storeId-upc": `online-${p.upc}`,
    storePrice: p.currentPrices.length > 0 ? p.currentPrices : [p.currentPrice],
    backroom: [0],
    salesFloor: [1],
    aisle: "",
    category: p.category,
    adjustedCategory: p.adjustedCategory,
    url: p.url,
    discountPercentage: p.discountPercentage,
    firstInserted: getOnlineFirstInserted(p),
    lastUpdates: p.lastUpdates.length > 0 ? p.lastUpdates : [p.lastUpdated],
    inStock: true,
  })) as ProductListItem[];
};

export const deriveCategories = (products: ProductListItem[]) =>
  adjustedCategoryCounts(products).map(({ id, count }) => ({ rootKey: id, subcategories: [], count }));

const getCurrentPrice = (product: ProductListItem) => {
  if (product.openBox && product.openBoxPrice?.length) {
    return product.openBoxPrice[product.openBoxPrice.length - 1] || 0;
  }
  return product.storePrice[product.storePrice.length - 1] || 0;
};

// "Hide cashback" only hides cashback deals whose store-price discount (before
// cashback) is under this threshold; deep markdowns that also carry cashback stay.
export const HIDE_CASHBACK_MIN_DISCOUNT = 20;

export const isHiddenCashbackDeal = (product: ProductListItem) =>
  getCashbackAmount(product) > 0 &&
  getEffectiveDiscountPercent(Number(product.msrp) || 0, getCurrentPrice(product)) < HIDE_CASHBACK_MIN_DISCOUNT;

const getPriceHistory = (product: ProductListItem) => {
  if (product.openBox && product.openBoxPrice?.length) {
    return product.openBoxPrice;
  }
  return product.storePrice;
};

export const hasPriceDrop = (product: ProductListItem) => {
  const history = getPriceHistory(product);
  const lastIndex = history.length - 1;
  return lastIndex > 0 && history[lastIndex] < history[lastIndex - 1];
};

const hasStockUpdate = (product: ProductListItem) => {
  const salesFloor = product.salesFloor;
  const backroom = product.backroom;
  const salesFloorChanged =
    Array.isArray(salesFloor) &&
    salesFloor.length >= 2 &&
    salesFloor[salesFloor.length - 1] !== salesFloor[salesFloor.length - 2];
  const backroomChanged =
    Array.isArray(backroom) &&
    backroom.length >= 2 &&
    backroom[backroom.length - 1] !== backroom[backroom.length - 2];
  return salesFloorChanged || backroomChanged;
};

const hasLocation = (product: ProductListItem) => {
  return Boolean(product.aisle && product.aisle.trim() !== "");
};

const isSoldByWalmartSeller = (product: ProductListItem) => {
  return product.sellerName?.toLowerCase() === "walmart.com";
};

const isSoldByProSeller = (product: ProductListItem) => {
  return Boolean(product.sellerName && product.sellerName.toLowerCase() !== "walmart.com");
};

const applyStoreScope = (products: ProductListItem[], selectedStoreId: string) => {
  if (selectedStoreId !== "all") {
    return products.filter((product) => product.storeId.includes(selectedStoreId));
  }
  return dedupeByUpcLowestPrice(products);
};

export const isAtLowestPrice = (product: ProductListItem) => {
  const prices = product.storePrice;
  if (!prices || prices.length < 3) return false;
  const current = prices[prices.length - 1];
  if (current <= 0) return false;
  // Must be strictly lower than all of the previous 3 data points
  const recentWindow = prices.slice(Math.max(0, prices.length - 4), prices.length - 1);
  if (!recentWindow.every((p) => p > current)) return false;
  // Must be the all-time lowest (no earlier price was lower)
  for (let i = 0; i < prices.length - 1; i++) {
    if (prices[i] > 0 && prices[i] < current) return false;
  }
  return true;
};

const getEffectiveDiscountPercentage = (product: ProductListItem) => {
  return getEffectiveDiscountPercent(Number(product.msrp) || 0, getCurrentPrice(product), getCashbackAmount(product), getCashbackMinQty(product));
};

const getOpenBoxDiscountPercentage = (product: ProductListItem) => {
  return Number(product.openBoxDiscountPercentage) || 0;
};

const getLastUpdated = (product: ProductListItem) => {
  return getLastUpdatedTimestamp({
    priceHistory: getPriceHistory(product),
    backroomHistory: product.backroom,
    salesFloorHistory: product.salesFloor,
    lastUpdates: product.lastUpdates,
    firstInserted: product.firstInserted,
    lastUpdated: product.lastUpdated,
  });
};

const getInventoryCount = (product: ProductListItem) => {
  const salesFloor = product.salesFloor?.[product.salesFloor.length - 1] || 0;
  const backroom = product.backroom?.[product.backroom.length - 1] || 0;
  return salesFloor + backroom;
};

const getDiscountAmount = (product: ProductListItem) => {
  const price = getCurrentPrice(product);
  const msrp = Number(product.msrp) || 0;
  return Math.max(msrp - price, 0);
};

const getSortValue = (product: ProductListItem, sortBy: string) => {
  switch (sortBy) {
    case "storePrice":
      return getCurrentPrice(product);
    case "msrp":
      return Number(product.msrp) || 0;
    case "discountAmount":
      return getDiscountAmount(product);
    case "discountPercentage":
      return getEffectiveDiscountPercentage(product);
    case "cashbackAmount":
      return getCashbackAmount(product);
    case "openBoxDiscountPercentage":
      return getOpenBoxDiscountPercentage(product);
    case "inventory":
      return getInventoryCount(product);
    case "firstAppeared":
      return product.firstInserted;
    case "lastSeen":
      return getLastSeenTimestamp(product.lastUpdates, product.firstInserted);
    case "lastUpdated":
    default:
      return getLastUpdated(product);
  }
};

const getSortDirection = (sortBy: string, direction?: "asc" | "desc") => {
  if (direction) return direction;
  return sortBy === "storePrice" ? "asc" : "desc";
};

const sortProducts = (
  products: ProductListItem[],
  sortBy: string,
  direction?: "asc" | "desc"
) => {
  if (sortBy === "bestDeal") {
    const ranked = sortByBestDeal(products);
    for (let i = 0; i < ranked.length; i += 1) products[i] = ranked[i];
    return;
  }
  const sortDirection = getSortDirection(sortBy, direction);
  const sortedProducts = products.map((product) => ({
    product,
    sortValue: getSortValue(product, sortBy),
  }));

  sortedProducts.sort((a, b) => {
    const aValue = a.sortValue;
    const bValue = b.sortValue;
    if (aValue === bValue) return 0;
    const delta = aValue - bValue;
    return sortDirection === "asc" ? delta : -delta;
  });

  for (let i = 0; i < sortedProducts.length; i += 1) {
    products[i] = sortedProducts[i].product;
  }
};

export const productHasSelectableOptions = (product: ProductListItem): boolean => {
  const options = (product as ProductListItem & {
    variantCriteria?: { sku?: string }[];
  }).variantCriteria;
  if (!options || options.length === 0) return false;
  return options.some(
    (option) =>
      !!option?.sku &&
      option.sku !== product.sku &&
      option.sku !== product.upc
  );
};

export const applyBaseFilters = (input: {
  products: ProductListItem[];
  selectedCategories: string[];
  showNewestOnly: boolean;
  showVariantsOnly?: boolean;
  hideCashback?: boolean;
  searchTerm: string;
  searchTokens?: string[];
  minStorePrice?: number;
  maxStorePrice?: number;
  minMsrp?: number;
  maxMsrp?: number;
  minDiscount?: number;
  showInStockOnly: boolean;
  retailer: string;
  isOnlineRetailer: boolean;
  now?: number;
}) => {
  const now = input.now ?? Date.now();
  let products = [...input.products];

  if (input.selectedCategories.length > 0 && input.selectedCategories[0] !== "__none__") {
    const selected = new Set(input.selectedCategories);
    products = products.filter((product) => matchesAdjustedCategories(product, selected));
  } else if (input.selectedCategories[0] === "__none__") {
    products = [];
  }

  if (input.showNewestOnly) {
    const threeDaysAgo = now - 3 * 24 * 60 * 60 * 1000;
    products = products.filter((product) => product.firstInserted > threeDaysAgo);
  }

  if (input.showVariantsOnly) {
    products = products.filter((product) => product.isVariant === true);
  }

  if (input.hideCashback) {
    products = products.filter((product) => !isHiddenCashbackDeal(product));
  }

  const searchTokens =
    input.searchTokens && input.searchTokens.length > 0
      ? input.searchTokens
      : input.searchTerm.trim()
      ? [normalizeSearchToken(input.searchTerm)]
      : [];
  if (searchTokens.length > 0) {
    products = products.filter((product) =>
      matchesSearchTokens(product, searchTokens)
    );
  }

  if (input.minStorePrice !== undefined || input.maxStorePrice !== undefined) {
    products = products.filter((product) => {
      const currentPrice = getCurrentPrice(product);
      const meetsMinPrice =
        input.minStorePrice === undefined || currentPrice >= input.minStorePrice;
      const meetsMaxPrice =
        input.maxStorePrice === undefined || currentPrice <= input.maxStorePrice;
      return meetsMinPrice && meetsMaxPrice;
    });
  }

  if (input.minMsrp !== undefined || input.maxMsrp !== undefined) {
    products = products.filter((product) => {
      const msrp = Number(product.msrp) || 0;
      const meetsMinMsrp = input.minMsrp === undefined || msrp >= input.minMsrp;
      const meetsMaxMsrp = input.maxMsrp === undefined || msrp <= input.maxMsrp;
      return meetsMinMsrp && meetsMaxMsrp;
    });
  }

  if (input.minDiscount !== undefined && input.minDiscount > 0) {
    products = products.filter(
      (product) => getEffectiveDiscountPercentage(product) >= (input.minDiscount || 0)
    );
  }

  if (input.showInStockOnly) {
    products = products.filter(
      (product) =>
        input.retailer === "bestbuy" ||
        input.isOnlineRetailer ||
        product.inStock
    );
  }

  return products;
};

export const dedupeByUpcLowestPrice = (products: ProductListItem[]) => {
  const productMap = new Map<string, ProductListItem>();
  products.forEach((product) => {
    const existing = productMap.get(product.upc);
    if (!existing) {
      productMap.set(product.upc, product);
      return;
    }
    const currentPrice = getCurrentPrice(product);
    const existingPrice = getCurrentPrice(existing);
    if (currentPrice < existingPrice) {
      productMap.set(product.upc, product);
    } else if (currentPrice === existingPrice) {
      if (getLastUpdated(product) > getLastUpdated(existing)) {
        productMap.set(product.upc, product);
      }
    }
  });
  return Array.from(productMap.values());
};

export const buildFavoritesAsProducts = (favorites: FavoriteProduct[]) => {
  return favorites.map((fav) => ({
    upc: fav.upc,
    sku: fav.sku || fav.upc,
    name: fav.name,
    msrp: fav.msrp || 0,
    onlinePrice: fav.onlinePrice || 0,
    image: fav.image,
    storeId: fav.storeId || "",
    "storeId-upc": `${fav.storeId || "saved"}-${fav.upc}`,
    storePrice: fav.storePrice?.length ? fav.storePrice : [0],
    backroom: [0],
    salesFloor: [fav.inStock ? 1 : 0],
    aisle: "",
    category: fav.category || "",
    adjustedCategory: fav.adjustedCategory,
    url: fav.url || "",
    discountPercentage: 0,
    firstInserted: fav.added || Date.now(),
    lastUpdates: [fav.lastUpdated || Date.now()],
    inStock: fav.inStock ?? true,
  })) as ProductListItem[];
};

export const applyStatusAndStoreFilters = (input: {
  products: ProductListItem[];
  productStatusFilter: string;
  selectedStoreId: string;
  retailer: string;
  isOnlineRetailer: boolean;
  now?: number;
}) => {
  const now = input.now ?? Date.now();
  let products = [...input.products];

  if (input.productStatusFilter === "new") {
    const twentyFourHoursAgo = now - 24 * 60 * 60 * 1000;
    products = products.filter((p) => p.firstInserted > twentyFourHoursAgo);
  } else if (input.productStatusFilter === "price_drop") {
    products = products.filter(hasPriceDrop);
  } else if (input.productStatusFilter === "stock_update") {
    if (input.retailer !== "bestbuy" && !input.isOnlineRetailer) {
      products = products.filter(hasStockUpdate);
    } else {
      products = [];
    }
  } else if (input.productStatusFilter === "with_location") {
    products = products.filter(hasLocation);
  } else if (input.productStatusFilter === "with_options") {
    products = products.filter(productHasSelectableOptions);
  } else if (input.productStatusFilter === "pickup_available") {
    products = products.filter((p) => p.pickupAvailable === true);
  } else if (input.productStatusFilter === "pickup_unavailable") {
    products = products.filter((p) => p.pickupAvailable === false);
  } else if (input.productStatusFilter === "on_sale") {
    products = products.filter((p) => (p as any).discountedProduct === true);
  } else if (input.productStatusFilter === "sold_by_walmart") {
    products = products.filter(isSoldByWalmartSeller);
  } else if (input.productStatusFilter === "sold_by_pro_seller") {
    products = products.filter(isSoldByProSeller);
  } else if (input.productStatusFilter === "lowest") {
    products = products.filter(isAtLowestPrice);
  } else if (input.productStatusFilter === "cashback") {
    products = products.filter((p) => getCashbackAmount(p) > 0);
  } else if (input.productStatusFilter === "ibotta") {
    products = products.filter(hasLiveIbottaOffer);
  } else if (input.productStatusFilter === "bogo") {
    products = products.filter((p) => getOfferType(p) === "bogo");
  } else if (input.productStatusFilter === "mix_match") {
    products = products.filter((p) => getOfferType(p) === "mix_match");
  } else if (input.productStatusFilter === "coupon") {
    products = products.filter((p) => p.storeId.endsWith("-COUPON"));
  }
  return applyStoreScope(products, input.selectedStoreId);
};

export const filterAndSortProducts = (input: {
  favoritesAsProducts: ProductListItem[];
  commonFilteredProducts: ProductListItem[];
  productStatusFilter:
    | "all"
    | "new"
    | "price_drop"
    | "open_box"
    | "stock_update"
    | "with_location"
    | "with_options"
    | "saved"
    | "pickup_available"
    | "pickup_unavailable"
    | "on_sale"
    | "sold_by_walmart"
    | "sold_by_pro_seller"
    | "lowest"
    | "cashback"
    | "ibotta"
    | "bogo"
    | "mix_match"
    | "coupon";
  sortBy: string;
  sortDirection?: "asc" | "desc";
  searchTerm: string;
  searchTokens?: string[];
  selectedStoreId: string;
  showVariantsOnly?: boolean;
  hideCashback?: boolean;
  allProducts?: ProductListItem[];
  retailer: string;
  isOnlineRetailer: boolean;
  now?: number;
}) => {
  const now = input.now ?? Date.now();

  if (input.productStatusFilter === "saved") {
    let products = [...input.favoritesAsProducts];
    if (input.showVariantsOnly) {
      products = products.filter((product) => product.isVariant === true);
    }
    if (input.hideCashback) {
      products = products.filter((product) => !isHiddenCashbackDeal(product));
    }
    const searchTokens =
      input.searchTokens && input.searchTokens.length > 0
        ? input.searchTokens
        : input.searchTerm.trim()
        ? [normalizeSearchToken(input.searchTerm)]
        : [];
    if (searchTokens.length > 0) {
      products = products.filter((product) =>
        matchesSearchTokens(product, searchTokens)
      );
    }
    if (input.selectedStoreId !== "all" && input.allProducts) {
      const upcsByStore = new Set(
        input.allProducts
          .filter((p) => p.storeId.includes(input.selectedStoreId))
          .map((p) => p.upc)
      );
      products = products.filter((product) => upcsByStore.has(product.upc));
    }
    sortProducts(products, input.sortBy, input.sortDirection);
    return products;
  }

  if (!input.commonFilteredProducts.length) return [];
  let products = [...input.commonFilteredProducts];

  if (input.productStatusFilter === "open_box") {
    products = applyStoreScope(
      products.filter((product) => product.openBox === true),
      input.selectedStoreId
    );
  } else {
    products = applyStatusAndStoreFilters({
      products,
      productStatusFilter: input.productStatusFilter,
      selectedStoreId: input.selectedStoreId,
      retailer: input.retailer,
      isOnlineRetailer: input.isOnlineRetailer,
      now,
    });
  }

  sortProducts(products, input.sortBy, input.sortDirection);

  return products;
};

export const computeProductStatusCountsFromBaseProducts = (input: {
  products: ProductListItem[];
  selectedStoreId: string;
  isOnlineRetailer: boolean;
  retailer: string;
  savedProducts: FavoriteProduct[];
  allProducts?: ProductListItem[];
  now?: number;
}) => {
  const now = input.now ?? Date.now();
  const products = applyStoreScope(input.products, input.selectedStoreId);
  const twentyFourHoursAgo = now - 24 * 60 * 60 * 1000;

  let newCount = 0;
  let priceDropCount = 0;
  let openBoxCount = 0;
  let stockUpdateCount = 0;
  let withLocationCount = 0;
  let withOptionsCount = 0;
  let pickupAvailableCount = 0;
  let pickupUnavailableCount = 0;
  let onSaleCount = 0;
  let soldByWalmartCount = 0;
  let soldByProSellerCount = 0;
  let lowestCount = 0;
  let cashbackCount = 0;
  let ibottaCount = 0;
  let bogoCount = 0;
  let mixMatchCount = 0;
  let couponCount = 0;

  const supportsStockUpdate =
    input.retailer !== "bestbuy" && !input.isOnlineRetailer;
  const supportsPickupStatus = input.retailer === "walmart";
  const supportsOnSaleStatus = input.retailer === "target";
  const supportsSellerCounts =
    input.retailer === "walmart_online" ||
    (input.retailer === "walmart" && input.isOnlineRetailer);

  for (const product of products) {
    if (product.firstInserted > twentyFourHoursAgo) newCount++;
    if (hasPriceDrop(product)) priceDropCount++;
    if (input.retailer === "bestbuy" && product.openBox === true) openBoxCount++;
    if (supportsStockUpdate && hasStockUpdate(product)) stockUpdateCount++;
    if (hasLocation(product)) withLocationCount++;
    if (productHasSelectableOptions(product)) withOptionsCount++;
    if (supportsPickupStatus && product.pickupAvailable === true) pickupAvailableCount++;
    if (supportsPickupStatus && product.pickupAvailable === false) pickupUnavailableCount++;
    if (supportsOnSaleStatus && (product as any).discountedProduct === true) onSaleCount++;
    if (supportsSellerCounts && isSoldByWalmartSeller(product)) soldByWalmartCount++;
    if (supportsSellerCounts && isSoldByProSeller(product)) soldByProSellerCount++;
    if (isAtLowestPrice(product)) lowestCount++;
    if (getCashbackAmount(product) > 0) cashbackCount++;
    if (hasLiveIbottaOffer(product)) ibottaCount++;
    const offerType = getOfferType(product);
    if (offerType === "bogo") bogoCount++;
    if (offerType === "mix_match") mixMatchCount++;
    if (product.storeId.endsWith("-COUPON")) couponCount++;
  }

  let savedCount = input.savedProducts?.length || 0;
  if (
    input.selectedStoreId !== "all" &&
    input.savedProducts?.length &&
    input.allProducts
  ) {
    const upcsByStore = new Set(
      input.allProducts
        .filter((product) => product.storeId.includes(input.selectedStoreId))
        .map((product) => product.upc)
    );
    savedCount = input.savedProducts.filter((favorite) =>
      upcsByStore.has(favorite.upc)
    ).length;
  }

  return {
    all: products.length,
    new: newCount,
    priceDrop: priceDropCount,
    openBox: openBoxCount,
    stockUpdate: stockUpdateCount,
    withLocation: withLocationCount,
    withOptions: withOptionsCount,
    saved: savedCount,
    pickupAvailable: pickupAvailableCount,
    pickupUnavailable: pickupUnavailableCount,
    onSale: onSaleCount,
    soldByWalmart: soldByWalmartCount,
    soldByProSeller: soldByProSellerCount,
    lowest: lowestCount,
    cashback: cashbackCount,
    ibotta: ibottaCount,
    bogo: bogoCount,
    mixMatch: mixMatchCount,
    coupon: couponCount,
  };
};

export const computeProductStatusCounts = (input: {
  products: ProductListItem[];
  selectedCategories: string[];
  showNewestOnly: boolean;
  showVariantsOnly?: boolean;
  hideCashback?: boolean;
  searchTerm: string;
  searchTokens?: string[];
  minStorePrice?: number;
  maxStorePrice?: number;
  minMsrp?: number;
  maxMsrp?: number;
  minDiscount?: number;
  showInStockOnly: boolean;
  selectedStoreId: string;
  isOnlineRetailer: boolean;
  retailer: string;
  savedProducts: FavoriteProduct[];
  allProducts?: ProductListItem[];
  now?: number;
}) => {
  const now = input.now ?? Date.now();
  const products = applyBaseFilters({
    products: input.products,
    selectedCategories: input.selectedCategories,
    showNewestOnly: input.showNewestOnly,
    showVariantsOnly: input.showVariantsOnly,
    hideCashback: input.hideCashback,
    searchTerm: input.searchTerm,
    searchTokens: input.searchTokens,
    minStorePrice: input.minStorePrice,
    maxStorePrice: input.maxStorePrice,
    minMsrp: input.minMsrp,
    maxMsrp: input.maxMsrp,
    minDiscount: input.minDiscount,
    showInStockOnly: input.showInStockOnly,
    retailer: input.retailer,
    isOnlineRetailer: input.isOnlineRetailer,
    now,
  });
  return computeProductStatusCountsFromBaseProducts({
    products,
    selectedStoreId: input.selectedStoreId,
    isOnlineRetailer: input.isOnlineRetailer,
    retailer: input.retailer,
    savedProducts: input.savedProducts,
    allProducts: input.allProducts,
    now,
  });
};

export const buildProductsByUpc = (products: ProductListItem[]) => {
  const map = new Map<string, ProductListItem[]>();
  products.forEach((product) => {
    const existing = map.get(product.upc) || [];
    existing.push(product);
    map.set(product.upc, existing);
  });
  return map;
};

export const getRetailerAccess = (input: {
  retailer: string;
  userRetailers: string[];
  retailerAccessOverrides?: Record<string, boolean>;
  isOnline?: boolean;
}) => {
  const overridden = getRetailerAccessOverride(
    { retailerAccessOverrides: input.retailerAccessOverrides },
    input.retailer,
  );
  if (input.userRetailers.length > 0 && overridden !== undefined) return overridden;
  // Online catalogs are a baseline entitlement: authenticated paid users can
  // never receive less access than guests or signed-free users.
  if (hasPublicOnlineFeedAccess(input.retailer, input.isOnline === true)) return true;
  // A plan sold for this retailer (e.g. dollar_general_basic, or an admin-added
  // plan entry) always unlocks it, matching the API's checkRetailerAccess order.
  // Tractor Supply, Northern Tool, Sam's Club and Ace stay Plus/Pro-only
  // regardless of a stray "<retailer>_basic" entitlement.
  const planSlug = normalizeRetailerAccessSlug(input.retailer);
  const plusOnlyRetailers = new Set(["tractor_supply", "northern_tool", "samsclub", "ace_hardware"]);
  if (
    !plusOnlyRetailers.has(planSlug) &&
    input.userRetailers.some((plan) => {
      const value = plan.toLowerCase().trim();
      return value === planSlug || value.startsWith(`${planSlug}_`);
    })
  ) {
    return true;
  }
  const specialRetailers = new Set([
    "dollar_general",
    "woot",
    "target",
    "walmart_online",
    "amazon",
    "lowes",
    "tractor_supply",
    "northern_tool",
    "samsclub",
    "ace_hardware",
    "kohls",
    "bestbuy",
    "target_online",
    "costco",
    "newegg",
    "gamestop",
    "bjs",
    "meijer",
    "menards",
    "academy",
    "walgreens",
    "zappos",
    "jcpenney",
    "belk",
    "michaels",
    "nike",
    "adidas",
    "reebok",
    "skechers",
    "puma",
    "underarmour",
    "columbia",
    "nordstromrack",
    "aldo",
    "vitacost",
    "journeys",
    "shopdisney",
    "ninjakitchen",
    "hottopic",
    "officedepot",
    "barnesnoble",
    "razer",
    "pacsun",
    "bathbodyworks",
    "staples",
  ]);
  if (specialRetailers.has(input.retailer)) {
    return hasPlusOrProPlan(input.userRetailers);
  }
  return input.userRetailers.some((userRetailer) =>
    userRetailer.includes(input.retailer)
  );
};

export const getRetailerLabel = (retailer: string) => {
  const retailerLabels: Record<string, string> = {
    walmart: "Walmart",
    walmart_online: "Walmart Online",
    amazon: "Amazon",
    lowes: "Lowe's",
    tractor_supply: "Tractor Supply",
    northern_tool: "Northern Tool",
    samsclub: "Sam's Club",
    ace_hardware: "Ace Hardware",
    kohls: "Kohl's",
    home_depot: "HD",
    dollar_general: "Dollar General",
    target: "Target",
    target_online: "Target Online",
    woot: "Woot",
    bestbuy: "Best Buy",
    costco: "Costco",
    newegg: "Newegg",
    gamestop: "GameStop",
    bjs: "BJ's Wholesale",
    meijer: "Meijer",
    menards: "Menards",
    academy: "Academy Sports",
    walgreens: "Walgreens",
    zappos: "Zappos",
    jcpenney: "JCPenney",
    belk: "Belk",
    michaels: "Michaels",
    nike: "Nike",
    adidas: "Adidas",
    reebok: "Reebok",
    skechers: "Skechers",
    puma: "Puma",
    underarmour: "Under Armour",
    columbia: "Columbia",
    nordstromrack: "Nordstrom Rack",
    aldo: "Aldo",
    vitacost: "Vitacost",
    journeys: "Journeys",
    shopdisney: "shopDisney",
    ninjakitchen: "Ninja Kitchen",
    hottopic: "Hot Topic",
    officedepot: "Office Depot",
    barnesnoble: "Barnes & Noble",
    razer: "Razer",
    pacsun: "PacSun",
    bathbodyworks: "Bath & Body Works",
    staples: "Staples",
    petsmart: "PetSmart",
    ulta: "Ulta Beauty",
    forever21: "Forever 21",
    gap: "Gap",
    petco: "Petco",
    americaneagle: "American Eagle",
    aerie: "Aerie",
    carters: "Carter's",
    abercrombie: "Abercrombie & Fitch",
    hollister: "Hollister",
    oldnavy: "Old Navy",
    bananarepublic: "Banana Republic",
    stevemadden: "Steve Madden",
    athleta: "Athleta",
    gapfactory: "Gap Factory",
    bananarepublicfactory: "Banana Republic Factory",
    boxlunch: "BoxLunch",
    verabradley: "Vera Bradley",
    footlocker: "Foot Locker",
    champs: "Champs Sports",
  };
  return retailerLabels[retailer] || retailer;
};

export interface TrendingCategory {
  category: string;
  count: number;
  avgDiscount: number;
  topProducts: ProductListItem[];
}

export const computeTrendingCategories = (
  products: ProductListItem[],
  now?: number,
  limit = 5
): TrendingCategory[] => {
  const ts = now ?? Date.now();
  const cutoff = ts - 48 * 60 * 60 * 1000;
  const recent = products.filter(
    (p) => p.firstInserted > cutoff && adjustedCategory(p)
  );
  if (!recent.length) return [];

  const groups = new Map<
    string,
    { total: number; discountSum: number; items: ProductListItem[] }
  >();
  for (const p of recent) {
    const cat = adjustedCategory(p);
    let g = groups.get(cat);
    if (!g) {
      g = { total: 0, discountSum: 0, items: [] };
      groups.set(cat, g);
    }
    g.total++;
    g.discountSum += getEffectiveDiscountPercentage(p);
    g.items.push(p);
  }

  const results: (TrendingCategory & { score: number })[] = [];
  for (const [cat, g] of groups) {
    const avg = g.discountSum / g.total;
    const top = [...g.items]
      .sort((a, b) => getEffectiveDiscountPercentage(b) - getEffectiveDiscountPercentage(a))
      .slice(0, 4);
    results.push({ category: cat, count: g.total, avgDiscount: Math.round(avg), topProducts: top, score: g.total * avg });
  }
  return results
    .sort((a, b) => b.score - a.score)
    .slice(0, limit)
    .map(({ score: _, ...rest }) => rest);
};

export const shouldLoadDollarGeneralCouponFeed = (input: {
  retailer: string;
  showHotDeals?: boolean;
  showPennyItems?: boolean;
  status: string;
}) =>
  input.retailer === "dollar_general" &&
  !input.showHotDeals &&
  !input.showPennyItems &&
  input.status !== "coupon" &&
  input.status !== "mix_match" &&
  input.status !== "bogo";

const browseProductKey = (product: ProductListItem) => product.upc || product.sku || "";

export const emptyProductBrowsePage = (): ProductBrowseResponse => ({
  products: [],
  total: 0,
  statusCounts: {
    all: 0,
    new: 0,
    priceDrop: 0,
    openBox: 0,
    stockUpdate: 0,
    withLocation: 0,
    withOptions: 0,
    pickupAvailable: 0,
    pickupUnavailable: 0,
    onSale: 0,
    soldByWalmart: 0,
    soldByProSeller: 0,
    lowest: 0,
    cashback: 0,
    ibotta: 0,
    bogo: 0,
    mixMatch: 0,
    coupon: 0,
  },
  categoryCounts: [],
  keywordCounts: {},
  storeCounts: {},
  hasMore: false,
});

export const mergeDollarGeneralBrowseResponses = (
  main: ProductBrowseResponse,
  coupons: ProductBrowseResponse,
): ProductBrowseResponse => {
  const seen = new Map<string, ProductListItem>();
  const order: string[] = [];
  for (const product of [...main.products, ...coupons.products]) {
    const key = browseProductKey(product);
    if (!key) continue;
    const existing = seen.get(key);
    if (!existing) {
      seen.set(key, product);
      order.push(key);
      continue;
    }
    if (product.storeId.endsWith("-COUPON") && !existing.storeId.endsWith("-COUPON")) {
      seen.set(key, product);
    }
  }
  const serverIncludesCoupons = (main.statusCounts?.coupon || 0) > 0;
  const hasMore = Boolean(main.hasMore || coupons.hasMore);
  return {
    products: order.map((key) => seen.get(key)!),
    total: serverIncludesCoupons ? main.total : main.total + coupons.total,
    statusCounts: serverIncludesCoupons
      ? {
          ...main.statusCounts,
          coupon: Math.max(main.statusCounts.coupon || 0, coupons.statusCounts.coupon || coupons.statusCounts.all || 0),
        }
      : {
          ...main.statusCounts,
          all: (main.statusCounts.all || 0) + (coupons.statusCounts.all || 0),
          new: (main.statusCounts.new || 0) + (coupons.statusCounts.new || 0),
          priceDrop: (main.statusCounts.priceDrop || 0) + (coupons.statusCounts.priceDrop || 0),
          stockUpdate: (main.statusCounts.stockUpdate || 0) + (coupons.statusCounts.stockUpdate || 0),
          lowest: (main.statusCounts.lowest || 0) + (coupons.statusCounts.lowest || 0),
          cashback: (main.statusCounts.cashback || 0) + (coupons.statusCounts.cashback || 0),
          ibotta: (main.statusCounts.ibotta || 0) + (coupons.statusCounts.ibotta || 0),
          bogo: (main.statusCounts.bogo || 0) + (coupons.statusCounts.bogo || 0),
          mixMatch: (main.statusCounts.mixMatch || 0) + (coupons.statusCounts.mixMatch || 0),
          coupon: coupons.statusCounts.coupon || coupons.statusCounts.all || 0,
        },
    categoryCounts: serverIncludesCoupons
      ? main.categoryCounts
      : mergeBrowseCategoryCounts(main.categoryCounts, coupons.categoryCounts),
    keywordCounts: serverIncludesCoupons
      ? main.keywordCounts
      : mergeBrowseKeywordCounts(main.keywordCounts, coupons.keywordCounts),
    storeCounts: { ...main.storeCounts, ...coupons.storeCounts },
    hasMore,
    nextCursor: hasMore
      ? JSON.stringify({
          main: main.hasMore ? main.nextCursor : undefined,
          coupon: coupons.hasMore ? coupons.nextCursor : undefined,
          mainDone: !main.hasMore,
          couponDone: !coupons.hasMore,
        })
      : undefined,
  };
};

const mergeBrowseCategoryCounts = (
  left: Array<{ id: string; count: number }> = [],
  right: Array<{ id: string; count: number }> = [],
) => {
  const counts = new Map<string, number>();
  for (const row of [...left, ...right]) {
    counts.set(row.id, (counts.get(row.id) || 0) + row.count);
  }
  return [...counts.entries()].map(([id, count]) => ({ id, count }));
};

const mergeBrowseKeywordCounts = (
  left: Record<string, number> = {},
  right: Record<string, number> = {},
) => {
  const counts = { ...left };
  for (const [key, value] of Object.entries(right)) {
    counts[key] = (counts[key] || 0) + value;
  }
  return counts;
};

export const parseDollarGeneralCouponCursor = (cursor?: string) => {
  if (!cursor) return { main: undefined, coupon: undefined, mainDone: false, couponDone: false };
  try {
    const parsed = JSON.parse(cursor) as {
      main?: string;
      coupon?: string;
      mainDone?: boolean;
      couponDone?: boolean;
    };
    if (parsed && typeof parsed === "object" && ("mainDone" in parsed || "couponDone" in parsed || "coupon" in parsed)) {
      return {
        main: parsed.main,
        coupon: parsed.coupon,
        mainDone: Boolean(parsed.mainDone),
        couponDone: Boolean(parsed.couponDone),
      };
    }
  } catch {
    // A leftover single-feed cursor still loads the regular page.
  }
  return { main: cursor, coupon: undefined, mainDone: false, couponDone: false };
};
